/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Chartist from 'vendors/chartist/chartist';
import Component from 'can/component/component';
import Map from 'scripts/models/base/map';
import template from './recap-summary.stache';
import DomEventsManager from 'scripts/dom-events-manager';
import appContext from 'scripts/app-context';

Component.extend({
    tag: 'recap-summary',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            data: {
                Type: Map,
                value: Map,
            },
            labels: {
                type: 'string',
                value: 'full', // full|short
            },
            chartData: {
                get() {
                    let sections = this.attr('data.sections');
                    if (sections && sections.length) {
                        let min = parseInt(this.attr('data.minimumValue') || 0);
                        let max = parseInt(this.attr('data.maximumValue') || 0);

                        let chartData = sections.serialize().reduce((a, b) => {
                            let list = b.values || [];

                            if (list.length < 18) {
                                let length = list.length;
                                let augmentationStartIndex = 18 - length;
                                list = list.concat(
                                    new Array(augmentationStartIndex),
                                );
                                _.fill(list, null, length);
                            }

                            return a.concat(list);
                        }, []);
                        // return inverse values to draw negatives on top
                        // convert to absolute value
                        let previousValue = null;
                        chartData = chartData.map((item) => {
                            let value = item && item.value;

                            if (value === 0) {
                                if (previousValue) {
                                    return previousValue > 0 ? -0.05 : 0.05;
                                } else {
                                    return value;
                                }
                            } else if (value > 0) {
                                previousValue = value;
                                return Math.round(max * value) * -1;
                            } else if (value < 0) {
                                previousValue = value;
                                return Math.round(min * value);
                            }

                            return item;
                        });
                        return chartData;
                    } else {
                        this.attr('data', {
                            noData: true,
                            minimumValue: '0',
                            maximumValue: '+0',
                            sections: () =>
                                _.range(1, 20).map((n) => ({
                                    title: n < 19 ? String(n) : '',
                                })),
                        });
                    }
                    return [];
                },
            },
            visible: {
                type: 'boolean',
                value: false,
            },
        },
    },
    events: {
        inserted() {
            appContext.model.bind('drawer-content-tabs-toggle', () => {
                if (this.element) {
                    this.renderCharts();
                }
            });

            this.viewModel.bind('visible', (ev, value) => {
                if (value) {
                    setTimeout(() => {
                        this.renderCharts(value);
                    });
                }
            });

            this.viewModel.bind('chartData', (ev, values) => {
                this.renderCharts();
            });

            this.renderCharts();

            DomEventsManager.subscribe(this);
        },

        removed: function() {
            DomEventsManager.unsubscribe(this);
        },

        onLayout: function(layout) {
            this.renderCharts(true);
        },

        renderCharts(force) {
            const min =
                parseInt(this.viewModel.attr('data.maximumValue') || 0) * -1;
            const max =
                parseInt(this.viewModel.attr('data.minimumValue') || 0) * -1;
            const chartData = this.viewModel.attr('chartData');

            if (
                !force &&
                _.isEqual(chartData, this.chartData) &&
                this.max === max &&
                this.min === min
            ) {
                return;
            }
            this.chartData = chartData;
            this.max = max;
            this.min = min;

            this.drawChart(
                this.element.find('.s-positive-chart .s-chart-area'),
                this.chartData,
                0,
                this.max,
            );
            this.drawChart(
                this.element.find('.s-negative-chart .s-chart-area'),
                this.chartData,
                this.min,
                0,
            );
        },

        drawChart($el, chartData, min, max) {
            let height = $el.height();

            new Chartist.Line(
                $el,
                {
                    series: [
                        {
                            name: 'chart-area',
                            data: chartData,
                        },
                    ],
                },
                {
                    chartPadding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                    },
                    fullWidth: true,
                    showArea: true,
                    showPoint: false,
                    height: height + 'px',
                    showLine: true,
                    high: max,
                    low: min,
                    axisY: {
                        showLabel: false,
                        offset: 0,
                        showGrid: false,
                        onlyInteger: true,
                        scaleMinSpace: 1,
                    },
                    axisX: {
                        showGrid: false,
                        showLabel: false,
                        offset: 0,
                    },
                    lineSmooth: Chartist.Interpolation.simple({
                        divisor: 2,
                    }),
                },
            );
        },
    },
});
